import { useEffect, useState } from "react";
import { Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SliderField } from "@/components/SliderField";
import { SearchableSelect } from "@/components/SearchableSelect";

const newExercise = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  exercise: "",
  series: "3",
  reps: "10",
});

const newActivity = (title = "", sport = "Running", minutes = "45", withExercise = false) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title,
  sport,
  minutes: String(minutes),
  distance_km: "",
  exercises: withExercise || sport === "Gimnasio" ? [newExercise()] : [],
});

export const FeedbackDialog = ({
  open,
  onOpenChange,
  onSubmit,
  busy,
  plannedTitle,
  plannedMinutes,
  sports = [],
  preferredSports = [],
  initialMode = "planned", // planned | actual
}) => {
  const [mode, setMode] = useState(initialMode);
  const [f, setF] = useState({ pain: 0, rpe: 6, rating: 4, notes: "" });
  const [activities, setActivities] = useState([]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const sportOpts = sports.length ? sports : ["Running", "Natación", "Ciclismo", "Caminata", "Gimnasio", "Otro"];
  const preferred = preferredSports.length ? preferredSports : [];

  useEffect(() => {
    if (open) {
      const actual = initialMode === "actual";
      setMode(actual ? "actual" : "planned");
      setF({ pain: 0, rpe: 6, rating: 4, notes: "" });
      // En «otra actividad» empezamos en Gimnasio con una fila de ejercicio lista para rellenar
      setActivities([newActivity(
        actual ? "" : (plannedTitle || ""),
        "Gimnasio",
        plannedMinutes || 45,
        actual,
      )]);
    }
  }, [open, plannedTitle, plannedMinutes, initialMode]);

  const updateAct = (id, patch) => {
    setActivities((list) => list.map((a) => {
      if (a.id !== id) return a;
      const next = { ...a, ...patch };
      // Al pasar a Gimnasio, si no hay ejercicios, crea la primera fila
      if (patch.sport === "Gimnasio" && !(next.exercises || []).length) {
        next.exercises = [newExercise()];
      }
      return next;
    }));
  };
  const removeAct = (id) => setActivities((list) => (list.length <= 1 ? list : list.filter((a) => a.id !== id)));
  const addAct = () => setActivities((list) => [...list, newActivity("", "Running", "30")]);

  const updateEx = (actId, exId, patch) => {
    setActivities((list) => list.map((a) => {
      if (a.id !== actId) return a;
      return { ...a, exercises: a.exercises.map((e) => (e.id === exId ? { ...e, ...patch } : e)) };
    }));
  };
  const addEx = (actId) => {
    setActivities((list) => list.map((a) => (a.id === actId ? { ...a, exercises: [...(a.exercises || []), newExercise()] } : a)));
  };
  const removeEx = (actId, exId) => {
    setActivities((list) => list.map((a) => (a.id === actId ? { ...a, exercises: a.exercises.filter((e) => e.id !== exId) } : a)));
  };

  const handleSubmit = () => {
    const payload = { pain: f.pain, rpe: f.rpe, rating: f.rating, notes: f.notes };
    if (mode === "actual") {
      const cleaned = [];
      for (const a of activities) {
        if (!a.title.trim()) {
          toast.error("Cada actividad necesita un nombre");
          return;
        }
        const mins = Number(a.minutes);
        if (!mins || mins < 5) {
          toast.error("Indica la duración en minutos (mín. 5)");
          return;
        }
        const ex = (a.exercises || [])
          .filter((e) => e.exercise.trim())
          .map((e) => ({
            exercise: e.exercise.trim(),
            series: e.series ? Number(e.series) : undefined,
            reps: e.reps?.trim() || undefined,
          }));
        cleaned.push({
          title: a.title.trim(),
          sport: a.sport,
          minutes: mins,
          distance_km: a.distance_km !== "" && a.distance_km != null ? Number(a.distance_km) : undefined,
          exercises: ex.length ? ex : undefined,
        });
      }
      if (!cleaned.length) {
        toast.error("Añade al menos una actividad");
        return;
      }
      payload.actual_activities = cleaned;
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {mode === "actual" ? "¿Qué has hecho en su lugar?" : "¿Qué tal la sesión?"}
          </DialogTitle>
          <DialogDescription>
            Planificado: «{plannedTitle || "entreno del día"}».
            {mode === "actual"
              ? " Indica la actividad (o varias) que sí has realizado."
              : " Confirma el plan o cambia a registrar otra cosa."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <div>
            <p className="text-sm font-semibold">¿Qué registramos?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                ["planned", "Como el plan"],
                ["actual", "Otra actividad"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  data-testid={`feedback-mode-${id}`}
                  className={`rounded-full px-3 py-2 text-xs font-semibold ${mode === id ? "bg-train text-white" : "bg-stone-100 text-stone-600"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {mode === "actual" && (
            <div className="space-y-3" data-testid="feedback-activities-list">
              {activities.map((a, i) => (
                <div key={a.id} className="space-y-3 rounded-2xl bg-train-soft/60 p-4" data-testid={`feedback-activity-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-train-ink">Actividad {i + 1}</p>
                    {activities.length > 1 && (
                      <button type="button" onClick={() => removeAct(a.id)} data-testid={`feedback-remove-activity-${i}`} className="text-stone-500 hover:text-ink">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-600">Nombre</label>
                    <Input
                      value={a.title}
                      onChange={(e) => updateAct(a.id, { title: e.target.value })}
                      placeholder="Ej.: Natación, circuito fuerza…"
                      className="rounded-2xl bg-white"
                      data-testid={`feedback-activity-title-${i}`}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-600">Deporte / tipo</label>
                    <SearchableSelect
                      value={a.sport}
                      onChange={(v) => updateAct(a.id, { sport: v })}
                      options={sportOpts}
                      preferred={preferred}
                      placeholder="Elegir deporte…"
                      searchPlaceholder="Buscar deporte…"
                      testid={`feedback-activity-sport-${i}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-stone-600">Duración (min)</label>
                      <Input type="number" inputMode="numeric" value={a.minutes} onChange={(e) => updateAct(a.id, { minutes: e.target.value })} className="rounded-2xl bg-white" data-testid={`feedback-activity-minutes-${i}`} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-stone-600">Distancia (km)</label>
                      <Input type="number" inputMode="decimal" step="0.1" placeholder="Opcional" value={a.distance_km} onChange={(e) => updateAct(a.id, { distance_km: e.target.value })} className="rounded-2xl bg-white" data-testid={`feedback-activity-distance-${i}`} />
                    </div>
                  </div>
                  <div className="space-y-2 rounded-2xl border border-train/20 bg-white/80 p-3" data-testid={`feedback-exercises-block-${i}`}>
                    <div>
                      <p className="text-xs font-bold text-train-ink">Ejercicios (en este mismo formulario)</p>
                      <p className="mt-0.5 text-[11px] text-stone-500">Escribe el nombre, series y reps aquí abajo. No se abre otra ventana.</p>
                    </div>
                    {(a.exercises || []).length > 0 && (
                      <div className="grid grid-cols-[1fr_4rem_4rem_auto] gap-2 px-0.5" aria-hidden>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-stone-500">Ejercicio</span>
                        <span className="text-center text-[10px] font-bold uppercase tracking-wide text-stone-500">Series</span>
                        <span className="text-center text-[10px] font-bold uppercase tracking-wide text-stone-500">Reps</span>
                        <span className="w-4" />
                      </div>
                    )}
                    {(a.exercises || []).map((ex, ei) => (
                      <div key={ex.id} className="grid grid-cols-[1fr_4rem_4rem_auto] items-center gap-2" data-testid={`feedback-exercise-${i}-${ei}`}>
                        <Input
                          placeholder="Ej.: Press banca"
                          aria-label="Nombre del ejercicio"
                          value={ex.exercise}
                          onChange={(e) => updateEx(a.id, ex.id, { exercise: e.target.value })}
                          className="rounded-xl bg-white text-sm"
                        />
                        <Input
                          type="number"
                          inputMode="numeric"
                          placeholder="3"
                          aria-label="Series"
                          title="Series"
                          value={ex.series}
                          onChange={(e) => updateEx(a.id, ex.id, { series: e.target.value })}
                          className="rounded-xl bg-white text-center text-sm"
                        />
                        <Input
                          placeholder="10"
                          aria-label="Repeticiones"
                          title="Repeticiones"
                          value={ex.reps}
                          onChange={(e) => updateEx(a.id, ex.id, { reps: e.target.value })}
                          className="rounded-xl bg-white text-center text-sm"
                        />
                        <button type="button" onClick={() => removeEx(a.id, ex.id)} aria-label="Quitar ejercicio" className="flex h-9 w-9 items-center justify-center rounded-xl text-stone-400 hover:bg-stone-100 hover:text-ink">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addEx(a.id)}
                      data-testid={`feedback-add-exercise-${i}`}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-train/40 bg-train-soft/40 py-2.5 text-sm font-semibold text-train-ink transition-colors hover:bg-train-soft"
                    >
                      <Plus className="h-4 w-4" /> Añadir otro ejercicio
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addAct} data-testid="feedback-add-activity" className="flex w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-train/40 py-3 text-sm font-semibold text-train-ink">
                <Plus className="h-4 w-4" /> Añadir otra actividad
              </button>
            </div>
          )}

          <SliderField label="Dolor durante la sesión" value={f.pain} min={0} max={10} onChange={set("pain")} testid="feedback-pain" hint="0 = nada · 10 = máximo dolor" />
          <SliderField label="Esfuerzo percibido (RPE)" value={f.rpe} min={1} max={10} onChange={set("rpe")} testid="feedback-rpe" hint="1 = muy fácil · 10 = al límite" />
          <div>
            <p className="text-sm font-semibold">Valoración</p>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => set("rating")(n)} data-testid={`feedback-star-${n}`} className="transition-transform active:scale-90">
                  <Star className={`h-8 w-8 ${n <= f.rating ? "fill-train text-train" : "text-stone-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <Textarea placeholder="Notas (opcional): cómo te has sentido, qué cambiarías…" value={f.notes} onChange={(e) => set("notes")(e.target.value)} className="rounded-2xl" data-testid="feedback-notes" />
        </div>
        <button
          disabled={busy}
          onClick={handleSubmit}
          data-testid="feedback-submit-btn"
          className="w-full rounded-full bg-train py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
        >
          {busy ? "Guardando…" : "Guardar sesión"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
